import {FieldError, Path, useFormContext} from "react-hook-form";
import Row from "react-bootstrap/Row";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import React, {ReactNode} from "react";
import {MANDATORY_ICON} from "../FormComponents";

/**
 * Props for this component
 */
interface DatePickerProps<T extends Record<string, any>> {
  label: string;
  helpText?: ReactNode;
  error: FieldError | undefined;
  fieldId: Path<T>,
  disabled?: boolean,
  mandatory?: boolean,
  customValidation?: (v: any) => true | string;
}

/**
 * This is a component that will add a date picker to a form. It was designed to be used as part of a form panel. You may
 * pass the following props
 *
 * @param label The text to show before the date picker. (By default this will show to the left of the input
 * @param helpText Subtext that shows on top of the question
 * @param error The fieldError from the React useForm specific for this field
 * @param fieldId The ID of the field as defined in the form interface
 * @param disabled <i>Optional</i> A boolean to mark the field as disabled
 * @param mandatory <i>Optional</i> A boolean to mark the field as mandatory
 * @param functionForExtraValidation  <i>Optional</i> A function where we can provide the component some more specific validations
 */
export default function DatePicker<T extends Record<string, any>>({
                                                                    label,
                                                                    helpText,
                                                                    error,
                                                                    fieldId,
                                                                    disabled = false,
                                                                    mandatory = false,
                                                                    customValidation
                                                                  }: DatePickerProps<T>) {
  const {register} = useFormContext<T>();

  // This is to be used when generating data-test-ids
  const fieldIdForDataTest = label.toLowerCase().replace(" ", "-");

  return <Form.Group as={Row} className="mb-2" controlId={`formHorizontal${fieldId}`}>
    {helpText && <Row>
      <Col sm={2}></Col>
      <Col sm={10} className={"question-help-text"}>
        {helpText}
      </Col>
    </Row>}
    <Row>
      <Form.Label column sm={2}>
        {label} {mandatory && <MANDATORY_ICON/>}
      </Form.Label>
      <Col sm={10}>
        <Form.Control
            type="date"
            disabled={disabled}
            {...register(fieldId, {
              required: mandatory ? `${label} is required` : false,
              setValueAs: (v: string) => {
                try {
                  return new Date(v).toISOString();
                } catch (e) {
                  //  Can most likely swallow this
                }
              },
              validate: customValidation ?
                  (v: any) => customValidation(v) :
                  undefined
            })}
            isInvalid={!!error}
            data-testid={fieldIdForDataTest}
        />
        <Form.Control.Feedback type="invalid" data-testid={`${fieldIdForDataTest}-error`}>
          {error?.message}
        </Form.Control.Feedback>
      </Col>
    </Row>
  </Form.Group>
}
